import React, { useEffect, useState } from 'react';
import { DrawerSection, DrawerSectionsWrapper, ResponsiveDrawer } from './ResponsiveDrawer';
import Text from 'antd/lib/typography/Text';
import { workflowsAndBranchesFromBuildDistroBucketRules } from '../../../helpers';
import { AppComponentProps, Nullable } from '../../../typescriptHelpers';
import { antGrey } from '../../styles/globalStyles';
import Space from 'antd/lib/space';
import Avatar from 'antd/lib/avatar';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import {
    selectBuildDistributionBucketById,
    selectIntegrationForCapability,
    selectPaginatedBucketBuildsLimitAndOffset,
} from '../../../selectors/selectors';
import { BuildDistributionBucket, IntegrationCapability } from '../../../types';
import { MobileAddEditBucketMembers } from '../AddEditBucketMembers';
import { EditOutlined, TabletOutlined, TeamOutlined } from '@ant-design/icons';
import PermissionedActionButton from '../../HOCs/PermissionedActionButton';
import { BucketBuildsList, WorkflowAndBranchTag } from '../CommonComponents';
import { RunwayUserAction } from '../../../helpers/aclHelpers';
import { useApp, useIsLoading } from '@/utils/hooks/helpers';
import { BucketDetailsTabTitle, BuildBucketDetailsTab } from '@/components/buildDistribution/BuildBucketDetails';
import Tabs from 'antd/lib/tabs';
import { createAppBuildDistroBucketRoute, createBuildDistroBucketBuildRoute } from '@/helpers/routeHelpers';
import { tryFetchPaginatedEntities } from '@/actions/entities';
import { FetchablePaginatedEntityType } from '@/entityType';
import { BuildDetailsDrawer } from '@/components/buildDistribution/BuildDetailsDrawer';
import { useHistory } from 'react-router-dom';

export interface BuildBucketDetailsDrawerProps extends AppComponentProps {
    open: boolean;
    bucket: BuildDistributionBucket;
    title: string;
    onClose: () => void;
    afterOpenChange?: (boolean) => void;
}

export const BuildBucketDetailsDrawer: React.FC<BuildBucketDetailsDrawerProps> = (props) => {
    const { title, open, onClose, appId, orgId, bucket, afterOpenChange } = props;

    const [isEditingMembers, setIsEditingMembers] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<BuildBucketDetailsTab>(BuildBucketDetailsTab.Builds);
    const [selectedBuildId, setSelectedBuildId] = useState<Nullable<string>>(null);

    const isLoadingBuilds = useIsLoading(FetchablePaginatedEntityType.BuildDistributionBucketBuilds, bucket.id);
    const currentBucketData = useSelector((state: RootState) => selectBuildDistributionBucketById(state, bucket.id));
    const buildIds = currentBucketData?.buildIds ?? [];

    const buildSystemIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.BuildSystem),
    );

    const hasNotFetchedAllBuilds = bucket?.totalBuildsCount > buildIds?.length;

    const dispatch = useDispatch();
    const app = useApp(props);
    const history = useHistory();

    const firstLetter = app.appSettings.appName.charAt(0);
    const { limit = 15, offset = 0 } = useSelector((state: RootState) => {
        return selectPaginatedBucketBuildsLimitAndOffset(state, bucket.id);
    });

    const loadMoreBuilds = React.useCallback(() => {
        dispatch(
            tryFetchPaginatedEntities(
                FetchablePaginatedEntityType.BuildDistributionBucketBuilds,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    bucketId: bucket.id,
                },
                limit,
                offset,
                bucket.id,
            ),
        );
    }, [limit, offset, bucket.id]);

    useEffect(() => {
        if (open && buildIds?.length === 0) {
            loadMoreBuilds();
        }
    }, [open]);

    return (
        <ResponsiveDrawer
            open={open}
            onClose={onClose}
            afterOpenChange={afterOpenChange}
            header={
                <Space size={20}>
                    <Avatar
                        shape={'square'}
                        size={36}
                        src={app.appSettings.appImageUrl}
                        style={{
                            flexShrink: 0,
                            boxShadow: 'var(--rw-shadow-sm)',
                        }}
                    >
                        {firstLetter}
                    </Avatar>
                    <Text
                        style={{
                            fontSize: 16,
                            color: antGrey.grey8,
                        }}
                    >
                        {title}
                    </Text>
                </Space>
            }
            content={
                <DrawerSectionsWrapper>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            paddingBottom: 8,
                        }}
                    >
                        {workflowsAndBranchesFromBuildDistroBucketRules(bucket?.rules)?.map((workflowAndBranch) => (
                            <WorkflowAndBranchTag
                                key={workflowAndBranch.workflowName + workflowAndBranch.branch}
                                workflowAndBranch={workflowAndBranch}
                                buildSystemIntegrationType={buildSystemIntegration?.id}
                            />
                        ))}
                    </div>
                    <div style={{ padding: '0, 16px' }}>
                        <Tabs
                            type={'card'}
                            activeKey={activeTab}
                            onChange={(key) => setActiveTab(key as BuildBucketDetailsTab)}
                            items={[
                                {
                                    key: BuildBucketDetailsTab.Builds,
                                    label: (
                                        <BucketDetailsTabTitle
                                            title={
                                                bucket.totalBuildsCount === undefined ?
                                                    'Builds'
                                                :   `Builds (${bucket.totalBuildsCount})`
                                            }
                                            isActive={activeTab === BuildBucketDetailsTab.Builds}
                                            icon={TabletOutlined}
                                        />
                                    ),
                                    children: (
                                        <div style={{ paddingTop: 16 }}>
                                            <BucketBuildsList
                                                appId={appId}
                                                orgId={orgId}
                                                buildIds={buildIds ?? []}
                                                isMobile={true}
                                                isLoading={isLoadingBuilds}
                                                onBuildSelection={(buildId) => {
                                                    setSelectedBuildId(buildId);
                                                }}
                                                destinationURLFactory={(selectedBuildId, bucketId) =>
                                                    createBuildDistroBucketBuildRoute(
                                                        orgId,
                                                        appId,
                                                        bucketId,
                                                        selectedBuildId,
                                                    )
                                                }
                                                onLoadMore={hasNotFetchedAllBuilds ? () => loadMoreBuilds() : null}
                                                showLoadMore={hasNotFetchedAllBuilds}
                                            />
                                            <BuildDetailsDrawer
                                                buildId={selectedBuildId}
                                                onCancel={() => {
                                                    setSelectedBuildId(null);
                                                }}
                                                appId={appId}
                                                orgId={orgId}
                                                bucketId={bucket.id}
                                                afterOpenChange={(open) => {
                                                    if (!open) {
                                                        const url = createAppBuildDistroBucketRoute(
                                                            orgId,
                                                            appId,
                                                            bucket.id,
                                                        );
                                                        history.replace(url);
                                                    }
                                                }}
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    key: BuildBucketDetailsTab.Members,
                                    label: (
                                        <BucketDetailsTabTitle
                                            title={`Members (${bucket?.members?.length ?? 0})`}
                                            icon={TeamOutlined}
                                            isActive={activeTab === BuildBucketDetailsTab.Members}
                                        />
                                    ),
                                    children: (
                                        <DrawerSection
                                            title="Members"
                                            extra={
                                                isEditingMembers ?
                                                    <PermissionedActionButton
                                                        type="link"
                                                        onClick={() => setIsEditingMembers(false)}
                                                    >
                                                        Done
                                                    </PermissionedActionButton>
                                                :   <PermissionedActionButton
                                                        actionType={RunwayUserAction.UABucketUpdateBucketMembers}
                                                        bucketId={bucket?.id}
                                                        type="link"
                                                        onClick={() => setIsEditingMembers(true)}
                                                        icon={<EditOutlined />}
                                                    >
                                                        Edit
                                                    </PermissionedActionButton>
                                            }
                                        >
                                            <MobileAddEditBucketMembers
                                                members={bucket.members ?? []}
                                                excludedMembers={[]}
                                                appId={appId}
                                                isEditing={isEditingMembers}
                                                orgId={orgId}
                                                bucketId={bucket.id}
                                            />
                                        </DrawerSection>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </DrawerSectionsWrapper>
            }
        />
    );
};
